import { useEffect, useRef } from 'react';
import { useAmplitudeTracking } from '@hooks/useAmplitudeTracking';
import { ANALYTICS_EVENTS } from '@lib/analytics-events';
import type { UseScrollTrackingOptions } from '../types';

const SCROLL_MILESTONES = [25, 50, 75, 100] as const;

/**
 * Hook for tracking scroll depth milestones
 *
 * Best practices applied:
 * - Passive event listeners for scroll (no jank)
 * - RAF debouncing to prevent multiple calculations per frame
 * - requestIdleCallback for non-blocking analytics dispatch
 * - Proper cleanup on unmount
 * - useRef for tracking state (avoids re-renders)
 */
export function useScrollTracking({
  questionCount,
  sortBy,
  isAuthenticated,
  enabled,
}: UseScrollTrackingOptions): void {
  const { trackEvent } = useAmplitudeTracking();
  const trackedMilestones = useRef(new Set<number>());
  const rafId = useRef<number | null>(null);

  useEffect(() => {
    // Skip if not enabled or not in browser
    if (!enabled || typeof window === 'undefined') return;

    const computeAndTrack = () => {
      rafId.current = null;

      const scrollTop = window.scrollY || document.documentElement.scrollTop || 0;
      const scrollHeight = document.documentElement.scrollHeight || 0;
      const viewportHeight = window.innerHeight || 0;
      const scrollable = Math.max(0, scrollHeight - viewportHeight);

      if (scrollable <= 0) return;

      const percent = Math.min(100, Math.max(0, Math.round((scrollTop / scrollable) * 100)));

      for (const milestone of SCROLL_MILESTONES) {
        if (percent >= milestone && !trackedMilestones.current.has(milestone)) {
          trackedMilestones.current.add(milestone);

          // Use requestIdleCallback for non-blocking analytics dispatch
          // Falls back to setTimeout for browsers without support
          const scheduleCallback =
            typeof requestIdleCallback !== 'undefined'
              ? requestIdleCallback
              : (cb: () => void) => setTimeout(cb, 0);

          scheduleCallback(() => {
            trackEvent(ANALYTICS_EVENTS.feed_scrolled, {
              scroll_depth_percent: milestone,
              question_count: questionCount,
              is_authenticated: isAuthenticated,
              ...(sortBy ? { sort_by: sortBy } : {}),
            });
          });
        }
      }
    };

    const handleScroll = () => {
      // Cancel previous frame to avoid queue buildup
      if (rafId.current !== null) return;
      rafId.current = window.requestAnimationFrame(computeAndTrack);
    };

    // Passive listener for better scroll performance
    window.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      window.removeEventListener('scroll', handleScroll);
      if (rafId.current !== null) {
        window.cancelAnimationFrame(rafId.current);
      }
    };
  }, [enabled, questionCount, sortBy, isAuthenticated, trackEvent]);

  // Reset milestones when sorting changes (new content view)
  useEffect(() => {
    trackedMilestones.current.clear();
  }, [sortBy]);
}
